import { useComparer } from '@/hooks/useComparer';
import { useExpandable } from '@/hooks/useExpandable';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import { ComparerScopedVariable, ComparerSubroutine, ComparerVariables } from '@/types/comparer';
import { SubroutineId } from '@/types/subroutine';
import { Stack, TableCell, TableRow, Typography } from '@mui/material';
import { Fragment, JSX, type FC } from 'react';
import { getChangedItems, getLocalVariablesSummary, getVisibleItems } from '../comparerUtils/comparerUtils';
import SectionHeader from '../comparerShared/SectionHeader';
import StatusRow from '../comparerShared/StatusRow';

type VariablesChangesProps = {
  subroutines: ComparerSubroutine[];
  variables: ComparerVariables;
};

type VariableChangesProps = {
  variables: ComparerScopedVariable[];
  scope: string;
  name?: string;
};
type LocalVariablesChangesProps = {
  localVariables: Record<SubroutineId, ComparerScopedVariable[]>;
  subroutines?: ComparerSubroutine[];
};
type VariablesHeaderProps = {
  variablesCount: number;
  hasChildren: boolean;
  scope: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  name?: string;
};

const VariablesHeader: FC<VariablesHeaderProps> = ({ scope, variablesCount, hasChildren, open, setOpen, name }) => (
  <SectionHeader open={open} setOpen={setOpen} expandable={hasChildren} indent={name ? 2 : 0}>
    <Typography variant='subtitle2' fontWeight={700} textTransform='capitalize'>
      {name ? `Subroutine: ${name}` : `Scope: ${scope}`}
    </Typography>
    <Typography variant='caption' color='text.secondary'>
      {variablesCount} variable{variablesCount !== 1 ? 's' : ''} changed
    </Typography>
  </SectionHeader>
);

export const LocalVariablesChanges: FC<LocalVariablesChangesProps> = ({ localVariables, subroutines }) => {
  const { showUnchanged } = useComparer();

  const localVariablesSummary = getLocalVariablesSummary(localVariables);
  const variablesCount = localVariablesSummary.added + localVariablesSummary.removed + localVariablesSummary.modified;
  const hasChildren = showUnchanged
    ? Object.values(localVariables).some((variables) => variables.length > 0)
    : variablesCount > 0;

  const [open, setOpen] = useExpandable(variablesCount > 0);
  const localEntries = useProgressiveList(Object.entries(localVariables));

  if (!hasChildren) return <Fragment />;
  return (
    <>
      <VariablesHeader
        scope='local'
        variablesCount={variablesCount}
        hasChildren={hasChildren}
        open={open}
        setOpen={(value) => setOpen(value)}
      />
      {open &&
        localEntries.map(([subroutineId, variables]) => {
          const name = subroutines?.find((sub) => sub.id.toString() === subroutineId)?.label;
          return (
            <VariableChanges key={subroutineId} variables={variables} scope={'local'} name={name || subroutineId} />
          );
        })}
    </>
  );
};

const VariableChanges: FC<VariableChangesProps> = ({ variables, scope, name }) => {
  const { showUnchanged } = useComparer();

  const changedVariables = getChangedItems(variables);
  const visibleVariables = getVisibleItems(variables, showUnchanged);

  const [open, setOpen] = useExpandable(changedVariables.length > 0);
  const progressiveVariables = useProgressiveList(visibleVariables);

  if (!visibleVariables.length) return <Fragment key={name}></Fragment>;
  return (
    <>
      <VariablesHeader
        variablesCount={changedVariables.length}
        hasChildren={visibleVariables.length > 0}
        scope={scope}
        open={open}
        setOpen={(value) => setOpen(value)}
        name={name}
      />
      {open && progressiveVariables.map((variable) => <VariableChange key={variable.id} variable={variable} />)}
    </>
  );
};

const VariableChange: FC<{ variable: ComparerScopedVariable }> = ({ variable }) => {
  const [open, setOpen] = useExpandable(variable.status === 'modified');

  const isModified = variable.status === 'modified';
  const isUnchanged = variable.status === 'unchanged';
  const hasChildren = isModified || isUnchanged;
  const type = !hasChildren ? ` (${variable.oldType || variable.newType})` : '';

  return (
    <>
      <StatusRow
        status={variable.status}
        title={`${variable.id}${type}`}
        label={variable.id}
        open={open}
        setOpen={hasChildren ? setOpen : undefined}
      />
      {open && hasChildren && (
        <TableRow>
          <TableCell size='small' sx={{ pl: 13 }}>
            <Stack direction='row' alignItems='center'>
              <Typography variant='caption' color='text.secondary'>
                type
              </Typography>
            </Stack>
          </TableCell>
          <TableCell size='small' sx={{ color: isModified ? 'error.main' : 'text.primary' }}>
            {variable.oldType}
          </TableCell>
          <TableCell size='small' sx={{ color: isModified ? 'success.main' : 'text.primary' }}>
            {variable.newType}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const VariablesChanges: FC<VariablesChangesProps> = ({ subroutines, variables }) => {
  const handleVariableChanges = (variables: ComparerVariables) => {
    const scopes = ['config', 'global', 'task', 'channel', 'session'] as const;
    const result: JSX.Element[] = [];
    scopes.forEach((scope) => {
      variables[scope].length &&
        result.push(<VariableChanges key={scope} variables={variables[scope]} scope={scope} />);
    });
    result.push(<LocalVariablesChanges key='local' localVariables={variables['local']} subroutines={subroutines} />);
    return result;
  };

  return handleVariableChanges(variables);
};

export default VariablesChanges;
